import {observer} from 'mobx-react-lite'
import React, {useCallback, useEffect, useRef} from 'react'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {ColumnChooser} from '@components/column-chooser'
import {IconButton} from '@components/common/base/buttons'
import {Menu, MenuItemData} from '@components/common/base/menu'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {crmStore} from '@components/crm/crm-store'
import {useBoardTabsStore} from '@components/upper-panel/board-tabs-store'
import {openWorkflowBoardSettings} from '@components/workflow/workflow-popup'
import {noPropagate, useForceUpdate, useGlobalKeyDown} from '@containers/hooks'

type BoardMenuVisibleItem =
  | 'rename'
  | 'move'
  | 'duplicate'
  | 'share'
  | 'settings'
  | 'delete'

const useBoardMenu = (
  board: Hub,
  visibleItems?: BoardMenuVisibleItem[]
): MenuItemData[] => {
  const [, store] = useBoardTabsStore()

  const menuItems: MenuItemData[] = []
  const isVisible = (id: BoardMenuVisibleItem) =>
    !visibleItems || visibleItems.includes(id)

  if (isVisible('rename')) {
    menuItems.push({
      id: 'rename',
      title: Lang.msg.rename,
      icon: 'IconEdit',
      onClick: () => store.startBoardRenaming(board.id),
      disabled: !store.canRenameBoard(board)
    })
  }

  if (isVisible('move')) {
    menuItems.push({
      id: 'move',
      title: Lang.msg.move,
      icon: 'IconCornerUpRight',
      onClick: () => store.openBoardMove(board.id),
      disabled: !store.canMoveBoard(board)
    })
  }

  if (isVisible('duplicate')) {
    menuItems.push({
      id: 'duplicate',
      title: Lang.msg.duplicate,
      icon: 'IconCopy',
      onClick: () => store.duplicateBoard(board.id),
      disabled: !store.canAddBoard(board)
    })
  }

  if (isVisible('share')) {
    menuItems.push({
      id: 'share',
      title: Lang.msg.share.boardSharing,
      icon: 'IconLink',
      onClick: () => store.openBoardSharing(board.id),
      disabled: !store.canShareBoard(board)
    })
  }

  if (isVisible('settings')) {
    menuItems.push({
      id: 'settings',
      title: Lang.context.board.settings,
      icon: 'IconSettings',
      onClick: () => store.openBoardSettings(board.id),
      disabled: !store.canEditBoardSettings(board)
    })
  }

  if (isVisible('delete')) {
    menuItems.push({
      id: 'delete',
      title: Lang.msg.delete,
      icon: 'IconTrash',
      onClick: () => store.deleteBoard(board.id),
      disabled: !store.canDeleteBoard(board)
    })
  }

  return menuItems
}

export const BoardMenu = observer(
  ({
    board,
    onToggle,
    className
  }: {
    board: Hub
    onToggle?: (value: boolean) => void
    className?: string
  }) => {
    const [state, store] = useBoardTabsStore()
    const ref = useRef(null)
    const active = useRef(false)
    const forceUpdate = useForceUpdate()
    useEffect(() => {
      onToggle?.(active.current)
    }, [active.current, onToggle])

    const open = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        if (e.shiftKey) {
          return openWorkflowBoardSettings(board.id)
        }
        active.current = true
        forceUpdate()
      },
      [board.id, onToggle]
    )
    const close = useCallback(() => {
      active.current = false
      forceUpdate()
    }, [])
    useGlobalKeyDown({
      escape: () => {
        const open = active.current
        close()
        return open
      }
    })

    let visibleItems: BoardMenuVisibleItem[]
    if (crmStore.isCrmBoard(board)) {
      visibleItems = ['delete', 'rename', 'settings']
    }

    const menuItems = useBoardMenu(board, visibleItems)

    return (
      <div className="w-fit h-fit" onClick={noPropagate}>
        <IconButton
          data-test="board-tab-menu"
          ref={ref}
          icon="IconDotsVertical"
          size="small"
          forcedState={active.current ? 'active' : undefined}
          color="panelIconSecondary"
          onClick={open}
          className={className}
        />
        {active.current && (
          <Dropdown
            reference={ref}
            hideArrow
            placement="bottom"
            fallbackPlacements={['bottom-start', 'bottom-end']}
            onVisibleChange={close}
            visible
            content={<Menu items={menuItems} onItemClicked={close} />}
            theme="new-menu"
          />
        )}
        {state.movedBoardId === board.id && (
          <Dropdown
            reference={ref}
            hideArrow
            placement="bottom"
            fallbackPlacements={['bottom-start', 'bottom-end']}
            onVisibleChange={store.closeBoardMove}
            visible
            content={
              <ColumnChooser
                className="!m-0"
                onChoose={store.moveBoard}
                chooseProjects
                singleColumn
              />
            }
          />
        )}
      </div>
    )
  }
)
